/**
 * Загрузка изображения в MinIO по presigned-ссылке.
 *
 * Файл идёт напрямую в хранилище, минуя backend: он только подписывает ссылку.
 * В форму попадает ключ объекта, а не URL — ссылки на чтение тоже временные
 * и генерируются на лету.
 */
import { useState } from "react";
import { InboxOutlined } from "@ant-design/icons";
import { Alert, Space, Spin, Typography, Upload } from "antd";

import { uploadImage } from "../lib/http";

interface Props {
  /** Ключ уже загруженного объекта. */
  value?: string;
  onChange?: (objectKey: string) => void;
  /** Presigned-ссылка на текущее изображение, если запись уже существует. */
  previewUrl?: string;
}

const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

export function ImageUpload({ value, onChange, previewUrl }: Props) {
  const [uploading, setUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const preview = localPreview ?? previewUrl;

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {preview && (
        <img
          src={preview}
          alt=""
          style={{
            width: "100%",
            maxHeight: 280,
            objectFit: "contain",
            borderRadius: 8,
            background: "#141414",
          }}
        />
      )}

      <Upload.Dragger
        accept={ACCEPTED.join(",")}
        showUploadList={false}
        disabled={uploading}
        beforeUpload={(file) => {
          if (!ACCEPTED.includes(file.type)) {
            setError("Поддерживаются только JPEG, PNG и WebP");
            return Upload.LIST_IGNORE;
          }

          setError(null);
          setUploading(true);
          setLocalPreview(URL.createObjectURL(file));

          void uploadImage(file)
            .then((objectKey) => onChange?.(objectKey))
            .catch((uploadError: unknown) => {
              setLocalPreview(null);
              setError(
                uploadError instanceof Error ? uploadError.message : "Не удалось загрузить файл",
              );
            })
            .finally(() => setUploading(false));

          // Загрузку выполняем сами, штатный механизм Upload не используем.
          return Upload.LIST_IGNORE;
        }}
      >
        {uploading ? (
          <Spin tip="Загружаем в хранилище…">
            <div style={{ height: 60 }} />
          </Spin>
        ) : (
          <>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Перетащите изображение или нажмите для выбора</p>
            <p className="ant-upload-hint">JPEG, PNG или WebP</p>
          </>
        )}
      </Upload.Dragger>

      {value && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Ключ объекта: <Typography.Text code>{value}</Typography.Text>
        </Typography.Text>
      )}

      {error && <Alert type="error" message={error} showIcon />}
    </Space>
  );
}
